import { Component, Inject, OnInit } from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {IQuestion} from '../../../../types/IQuestionManager';
import { IDialogConfirm } from '../../../assist-manager-landing/prompt-profile/prompt-profile.component';
import { DialogConfirmComponent } from '../../../dialog-confirm/dialog-confirm.component';

@Component({
    selector: 'app-question-modal',
    templateUrl: './question-modal.component.html',
    styleUrls: ['./question-modal.component.scss'],
    standalone: false
})
export class QuestionModalComponent implements OnInit {
  form!: FormGroup;

  sortedEntities = [
    { objectName: 'Candidate', label: 'Candidate' },
    { objectName: 'ClientContact', label: 'Contact' },
  ];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<QuestionModalComponent>,
    private matDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: IQuestionModalData
  ) {}

  ngOnInit(): void {
    const defaultEntity = this.data.question.entityType?.trim() || 'Candidate';

    const meta =
      typeof this.data.question.meta === 'string'
        ? JSON.parse(this.data.question.meta)
        : this.data.question.meta;

    this.form = this.fb.group({
      displayName: [meta?.question || '', [Validators.required]],
      action: [meta?.destination || ''],
      entityType: [defaultEntity]
    });
  }

  private sanitizeQuestion(input: string): string {
    return input
      .replace(/[\r\n]/g, '') // Remove newlines
      .replace(/"/g, '') // Remove double quotes
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
  }

  get isNewQuestion(): boolean {
    return !this.data.question.sortKey || this.data.question.sortKey.trim() === '';
  }

  get allowSave(): boolean {
    return this.form.valid && !this.form.errors;
  }

  discardLocalChanges(e: any = null): void {
    if (e) e.preventDefault();

    if (this.form.dirty) {
      const confirmDialogRef: MatDialogRef<DialogConfirmComponent> = this.matDialog.open(
        DialogConfirmComponent,
        {
          width: '45vw',
          height: 'auto',
          autoFocus: true,
          data: {
            title: `Changes made`,
            titleIcon: 'info_outline',
            message: this.allowSave
              ? 'Would you like to save your changes?'
              : 'Would you like to go back, amend and save?',
            positiveText: 'Discard',
            negativeText: 'Go Back',
            alternativeText: this.allowSave ? 'Save' : null,
          } as IDialogConfirm,
        }
      );
      confirmDialogRef.afterClosed().subscribe((takeAction: any) => {
        if (!takeAction) return;
        else this.dialogRef.close(false);
        if (takeAction === 'Save') {
          this.saveQuestion();
        }
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  saveQuestion(): void {
    const originalMeta =
      (typeof this.data.question.meta === 'object' && this.data.question.meta !== null)
        ? this.data.question.meta
        : {} as { question: string; type: string; destination: string; additionalRestrictions?: string; style: string };

    const sanitizedQuestion = this.sanitizeQuestion(this.form.value.displayName);

    const updatedQuestion: IQuestion = {
      ...this.data.question,
      meta: {
        ...originalMeta,
        question: sanitizedQuestion,
        destination: this.form.value.action,
        type: this.form.value.entityType,
        additionalRestrictions: originalMeta.additionalRestrictions || '',
        style: 'INFORMATION',
      },
      entityType: this.form.value.entityType,
    };

    updatedQuestion.meta = JSON.stringify(updatedQuestion.meta);

    if (!this.data.question.sortKey || this.data.question.sortKey.trim() === '') {
      updatedQuestion.sortKey = `Question-${this.data.commonGuid}-${this.form.value.entityType}-${crypto.randomUUID()}`;
    }

    this.dialogRef.close({
      action: 'save',
      question: updatedQuestion,
    } as IModalCloseEventDataQuestions);
  }

}

export interface IModalCloseEventDataQuestions {
  action: 'save' | 'delete';
  question: IQuestion;
}

export interface IQuestionModalData {
  question: IQuestion;
  commonGuid: string;
}
